/**
 * FEATURE: BATTLE_MODE
 *
 * In-memory + persisted cache of all BattleGame objects.
 * Loaded once at app start (when user is logged in and battle mode enabled)
 * so opening a battle doesn't require a per-game network request.
 */

import Parse from "../parseClient";

const STORAGE_KEY = "wordhit_battle_games_cache";
const VERSION_KEY = "wordhit_battle_games_version";

/** @type {Array<{ battleGameId: string, idNumber: number, theme: string, themeNL: string, words: string[], wordsNL: string[], status: string, createdAt?: Date }>} */
let memoryCache = [];

function getStoredVersion() {
  try {
    const raw = localStorage.getItem(VERSION_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return v && typeof v.count === "number" ? v : null;
  } catch {
    return null;
  }
}

function setStoredVersion(count, maxUpdatedAt) {
  try {
    localStorage.setItem(VERSION_KEY, JSON.stringify({ count, maxUpdatedAt: maxUpdatedAt ?? null }));
  } catch (e) {
    console.warn("[battleGamesCache] Failed to persist version:", e);
  }
}

/**
 * Load all BattleGames from Parse and store in memory + localStorage.
 * Calls getBattleGamesVersion first; only refetches full list when version (count or maxUpdatedAt) changed.
 * @returns {Promise<{ success: boolean, count: number, refreshed?: boolean }>}
 */
export async function loadAllBattleGames() {
  if (!Parse || !Parse.Cloud) {
    console.warn("[battleGamesCache] Parse not ready, skip load");
    return { success: false, count: 0 };
  }

  try {
    const versionResult = await Parse.Cloud.run("getBattleGamesVersion");
    if (!versionResult || !versionResult.success) {
      console.warn("[battleGamesCache] getBattleGamesVersion failed, will refetch");
    } else {
      const stored = getStoredVersion();
      const sameVersion =
        stored &&
        stored.count === versionResult.count &&
        String(stored.maxUpdatedAt || "") === String(versionResult.maxUpdatedAt || "");
      if (sameVersion) {
        restoreFromStorage();
        if (memoryCache.length > 0) {
          console.log("[battleGamesCache] NIET vanuit de cloud: versie ongewijzigd, bestaande cache gebruikt (" + memoryCache.length + " BattleGames)");
          return { success: true, count: memoryCache.length, refreshed: false };
        }
      }
    }

    console.log("[battleGamesCache] WEL vanuit de cloud: BattleGames ophalen...");
    const result = await Parse.Cloud.run("getAllBattleGames");

    if (!result || !result.success || !Array.isArray(result.battleGames)) {
      console.warn("[battleGamesCache] getAllBattleGames returned invalid data", result);
      return { success: false, count: 0 };
    }

    memoryCache = result.battleGames;

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryCache));
    } catch (e) {
      console.warn("[battleGamesCache] Failed to persist to localStorage:", e);
    }

    const maxUpdatedAt = memoryCache.reduce((max, g) => {
      const u = g.updatedAt;
      if (!u) return max;
      return !max || String(u) > String(max) ? u : max;
    }, null);
    setStoredVersion(memoryCache.length, maxUpdatedAt);

    console.log("[battleGamesCache] WEL vanuit de cloud opgehaald: " + memoryCache.length + " BattleGames geladen en gecached");
    return { success: true, count: memoryCache.length, refreshed: true };
  } catch (error) {
    console.error("[battleGamesCache] Failed to load BattleGames:", error);
    return { success: false, count: 0 };
  }
}

/**
 * Restore cache from localStorage (e.g. on app load before network fetch).
 * Does not overwrite memory cache if it already has data.
 */
export function restoreFromStorage() {
  if (memoryCache.length > 0) return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        memoryCache = parsed;
        console.log("[battleGamesCache] Restored", memoryCache.length, "BattleGames from storage");
      }
    }
  } catch (e) {
    console.warn("[battleGamesCache] Failed to restore from localStorage:", e);
  }
}

/**
 * Get one BattleGame by objectId (battleGameId), in the shape expected by buildGameFromPuzzle.
 * @param {string} battleGameId - Parse objectId of the BattleGame
 * @param {string} langString - "nl" or "en"
 * @returns {{ theme: string, words: string[], idNumber: number, battleGameId: string } | null}
 */
export function getBattleGame(battleGameId, langString) {
  restoreFromStorage();
  const game = memoryCache.find((g) => g.battleGameId === battleGameId);
  if (!game) return null;

  const theme = langString === "nl" ? game.themeNL : game.theme;
  const words = langString === "nl" ? game.wordsNL : game.words;
  if (!theme || !words || !Array.isArray(words)) return null;

  return {
    theme,
    words,
    idNumber: game.idNumber ?? 1,
    battleGameId: game.battleGameId,
  };
}

/**
 * Get one BattleGame by idNumber (for fast "next game" when cache is loaded).
 * @param {number} idNumber - idNumber of the BattleGame
 * @param {string} langString - "nl" or "en"
 * @returns {{ theme: string, words: string[], idNumber: number, battleGameId: string } | null}
 */
export function getBattleGameByIdNumber(idNumber, langString) {
  restoreFromStorage();
  const game = memoryCache.find((g) => (g.idNumber ?? 1) === Number(idNumber));
  if (!game) return null;

  const theme = langString === "nl" ? game.themeNL : game.theme;
  const words = langString === "nl" ? game.wordsNL : game.words;
  if (!theme || !words || !Array.isArray(words)) return null;

  return {
    theme,
    words,
    idNumber: game.idNumber ?? 1,
    battleGameId: game.battleGameId,
  };
}

/**
 * Whether the cache has been populated (in memory or after restore).
 */
export function isCacheLoaded() {
  restoreFromStorage();
  return memoryCache.length > 0;
}
